import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectCurrentGroup,
  selectGroupsStatus,
  selectMembers,
} from "../features/members/members-slice";
import { CreateGroupRequest } from "../features/members/contracts/CreateGroupRequest";
import { UpdateGroupRequest } from "../features/members/contracts/UpdateGroupRequest";
import {
  createGroup,
  updateGroup,
  fetchGroupById,
  fetchMembers,
  fetchGroupMembers,
  addMemberToGroup,
  removeMemberFromGroup,
} from "../features/members/slice-extensions/members-slice-extension";
import { Member } from "../features/members/models/Member";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import Tabs from "../components/Tabs";
import Input from "../components/input/Input";
import Textarea from "../components/textarea/Textarea";
import {
  showSuccessNotification,
  showErrorNotification,
} from "../utils/notifications";

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
};

const MEMBER_COLORS = [
  "#367D7D", "#2E6B6B", "#3D8F8F", "#2A7A5E",
  "#4A8C6F", "#3B7A9E", "#2D6E8A", "#458B74",
];

const getMemberColor = (firstName: string, lastName: string): string => {
  return MEMBER_COLORS[hashString(firstName + lastName) % MEMBER_COLORS.length];
};

const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const CreateEditGroup: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currentGroup = useAppSelector(selectCurrentGroup);
  const groupsStatus = useAppSelector(selectGroupsStatus);
  const allMembers = useAppSelector(selectMembers);

  const isEditMode = Boolean(id);
  const isFetching = groupsStatus === LoadingStatus.Loading;

  // Tabs (only shown in create mode)
  const tabs = ["Member", "Group"];
  const handleTabSelect = (index: number) => {
    if (index === 0) {
      navigate(ROUTES.CREATE_MEMBER);
    }
  };

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [nameError, setNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Member assignment state
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  // Fetch group data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchGroupById(id));
    }
  }, [isEditMode, id, dispatch]);

  // Fetch all members on mount
  useEffect(() => {
    dispatch(fetchMembers());
  }, [dispatch]);

  // Load group members in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchGroupMembers(id)).then((result) => {
        if (fetchGroupMembers.fulfilled.match(result)) {
          setSelectedMembers(result.payload);
        }
      });
    }
  }, [isEditMode, id, dispatch]);

  // Pre-fill form in edit mode
  useEffect(() => {
    if (isEditMode && currentGroup) {
      setName(currentGroup.name);
      setDescription(currentGroup.description || "");
      setTags(currentGroup.tags || []);
    }
  }, [isEditMode, currentGroup]);

  // Filtered members for dropdown (exclude already selected)
  const filteredMembers = useMemo(() => {
    const selectedIds = new Set(selectedMembers.map((m) => m.id));
    return allMembers.filter((m) => {
      if (selectedIds.has(m.id)) return false;
      if (!memberSearchText.trim()) return true;
      const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
      return fullName.includes(memberSearchText.toLowerCase());
    });
  }, [allMembers, selectedMembers, memberSearchText]);

  // Add member to selection
  const handleSelectMember = (member: Member) => {
    setSelectedMembers((prev) => [...prev, member]);
    setMemberSearchText("");
    setShowMemberDropdown(false);
  };

  // Remove member from selection
  const handleRemoveMember = (memberId: string) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (nameError) {
      setNameError("");
    }
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDescription(e.target.value);
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = tagInput.trim();
      if (trimmed && !tags.includes(trimmed)) {
        setTags([...tags, trimmed]);
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate
    if (!name.trim()) {
      setNameError("Group name is required.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && id) {
        const request: UpdateGroupRequest = {
          name: name.trim(),
          description: description.trim() || null,
          tags,
        };

        const result = await dispatch(updateGroup({ id, request }));

        if (updateGroup.fulfilled.match(result)) {
          // Handle member changes: fetch current group members to diff
          const currentMembersResult = await dispatch(fetchGroupMembers(id));
          if (fetchGroupMembers.fulfilled.match(currentMembersResult)) {
            const currentMemberIds = new Set(
              currentMembersResult.payload.map((m) => m.id)
            );
            const selectedMemberIds = new Set(selectedMembers.map((m) => m.id));

            // Add new members
            const membersToAdd = selectedMembers.filter(
              (m) => !currentMemberIds.has(m.id)
            );
            for (const member of membersToAdd) {
              await dispatch(
                addMemberToGroup({ groupId: id, memberId: member.id })
              );
            }

            // Remove old members
            const membersToRemove = currentMembersResult.payload.filter(
              (m) => !selectedMemberIds.has(m.id)
            );
            for (const member of membersToRemove) {
              await dispatch(
                removeMemberFromGroup({ groupId: id, memberId: member.id })
              );
            }
          }

          showSuccessNotification("Group updated successfully!");
          navigate(ROUTES.GROUP_DETAILS.replace(":id", id));
        } else {
          showErrorNotification("Failed to update group. Please try again.");
        }
      } else {
        const request: CreateGroupRequest = {
          name: name.trim(),
          description: description.trim() || null,
          tags,
        };

        const result = await dispatch(createGroup(request));

        if (createGroup.fulfilled.match(result)) {
          const newGroup = result.payload;

          // Add members to the newly created group
          for (const member of selectedMembers) {
            await dispatch(
              addMemberToGroup({ groupId: newGroup.id, memberId: member.id })
            );
          }

          showSuccessNotification("Group created successfully!");
          navigate(ROUTES.GROUP_DETAILS.replace(":id", newGroup.id));
        } else {
          showErrorNotification("Failed to create group. Please try again.");
        }
      }
    } catch (error) {
      const errorMessage = isEditMode
        ? "Failed to update group. Please try again."
        : "Failed to create group. Please try again.";
      showErrorNotification(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(ROUTES.GROUP_DETAILS.replace(":id", id));
    } else {
      navigate(ROUTES.GROUPS);
    }
  };

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            <Breadcrumbs />

            <div className="space-y-4 py-6 lg:space-y-6">
              <h1 className="text-grey-10 dark:text-white">
                {isEditMode ? "Edit Group" : "Create Group"}
              </h1>

              {/* Tabs (only in create mode) */}
              {!isEditMode && (
                <Tabs
                  tabs={tabs}
                  selectedIndex={1}
                  onSelect={handleTabSelect}
                />
              )}

              {isFetching && isEditMode ? (
                <div className="text-center py-4">
                  <p className="text-grey-30 dark:text-grey-70">
                    Loading group data...
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6 lg:space-y-4"
                >
                  {/* Group Name */}
                  <Input
                    label="Group Name"
                    type="text"
                    name="group_name"
                    id="group_name"
                    placeholder="Enter group name here..."
                    value={name}
                    onChange={handleNameChange}
                    error={nameError}
                    disabled={isSubmitting}
                  />

                  {/* Description */}
                  <Textarea
                    label="Description (optional)"
                    name="group_description"
                    id="group_description"
                    placeholder="Add group description here..."
                    value={description}
                    onChange={handleDescriptionChange}
                    disabled={isSubmitting}
                  />

                  {/* Tags */}
                  <div className="my-4">
                    <label className="block text-sm font-medium mb-1 text-grey-30 dark:text-white">
                      Tags (optional):
                    </label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Type a tag and press Enter..."
                      value={tagInput}
                      onChange={handleTagInputChange}
                      onKeyDown={handleTagInputKeyDown}
                      disabled={isSubmitting}
                    />
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-primary-90 dark:bg-primary-20 text-primary-30 dark:text-primary-90"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className="ml-1 text-primary-40 dark:text-primary-80 hover:text-primary-20 dark:hover:text-white"
                              disabled={isSubmitting}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M18 6L6 18M6 6l12 12"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Divider */}
                  <hr className="border-grey-70 dark:border-primary-20" />

                  {/* Member Enrollment Section */}
                  <div className="space-y-4">
                    <h3 className="text-grey-10 dark:text-grey-90 font-bold">
                      Members ({selectedMembers.length})
                    </h3>
                    <p className="text-grey-30 dark:text-grey-70 text-sm">
                      Add members to this group.
                    </p>

                    {/* Member Search */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-grey-30 dark:text-white mb-1">
                        Add Members (optional):
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder="Search members..."
                        value={memberSearchText}
                        onChange={(e) => {
                          setMemberSearchText(e.target.value);
                          setShowMemberDropdown(true);
                        }}
                        onFocus={() => setShowMemberDropdown(true)}
                        onBlur={() => {
                          setTimeout(() => setShowMemberDropdown(false), 200);
                        }}
                        disabled={isSubmitting}
                      />

                      {/* Member Dropdown */}
                      {showMemberDropdown && filteredMembers.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto rounded-lg border border-grey-70 dark:border-primary-20 bg-white dark:bg-primary-20 shadow-lg">
                          {filteredMembers.map((member) => (
                            <button
                              key={member.id}
                              type="button"
                              className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors"
                              onClick={() => handleSelectMember(member)}
                            >
                              <div
                                className="flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-bold shrink-0"
                                style={{ backgroundColor: getMemberColor(member.firstName, member.lastName) }}
                              >
                                {getInitials(member.firstName, member.lastName)}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {member.firstName} {member.lastName}
                                </p>
                                {member.email && (
                                  <p className="text-xs text-grey-50 dark:text-grey-70 truncate">
                                    {member.email}
                                  </p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Selected Members */}
                    {selectedMembers.length > 0 && (
                      <ul className="space-y-2">
                        {selectedMembers.map((member) => (
                          <li
                            key={member.id}
                            className="flex items-center gap-3 p-2 rounded-lg bg-grey-95 dark:bg-primary-20"
                          >
                            <div
                              className="flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-bold shrink-0"
                              style={{ backgroundColor: getMemberColor(member.firstName, member.lastName) }}
                            >
                              {getInitials(member.firstName, member.lastName)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-grey-10 dark:text-grey-90 truncate">
                                {member.firstName} {member.lastName}
                              </p>
                              {member.email && (
                                <p className="text-xs text-grey-50 dark:text-grey-70 truncate">
                                  {member.email}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(member.id)}
                              className="text-grey-50 hover:text-red-500 dark:text-grey-70 dark:hover:text-red-400 shrink-0 p-1"
                              title="Remove member"
                              disabled={isSubmitting}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Show All Members link */}
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MEMBERS)}
                      className="text-primary-40 dark:text-primary-90 text-sm hover:underline"
                    >
                      Show All Members &gt;
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-start gap-3">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? isEditMode
                          ? "Saving..."
                          : "Creating..."
                        : isEditMode
                        ? "Save Changes"
                        : "Create Group"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreateEditGroup;
